import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import {
  getProfile,
  patchProfile,
  patchOccupation,
  patchProfileImage,
  deleteProfileImage,
  getWorkCategoryList,
  patchWorkCategories,
} from '../../apis/member'
import { setErrors } from '../common/errorsSlice'
import {
  Profile,
  EditProfileRequest,
  Occupation,
  GetOccupationListResponse,
  WorkStyle,
  GetWorkCategoryListResponse,
  PatchWorkCategoriesRequest,
} from '../../types/member'

interface ErrorCallback {
  errors: {
    column_name: string,
    message: string[],
  }[]
}

interface ProfileState extends Profile {
  occupationList: Occupation[]
  work_style_list: WorkStyle[],
  isLoading: boolean
}

const initialState: ProfileState = {
  id: 0,
  name: '',
  kana: null,
  email: '',
  contract: null,
  place: null,
  occupation_id: null,
  occupationList: [],
  profile_image_url: null,
  base_work_time: 0,
  base_active_time: 0,
  base_inactive_time: 0,
  work_style_ids: [],
  work_style_list: [],
  work_style_updated_at: null,
  viewable_my_latest_weekly_report: false,
  isLoading: true,
}

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    getProfileSuccess: (state, action: PayloadAction<Profile>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      return state
    },
    patchProfileSuccess: (state, action: PayloadAction<EditProfileRequest>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
    requestFinish: (state) => {
      state.isLoading = false
    },
    getOccupationListSuccess: (state, action: PayloadAction<GetOccupationListResponse>) => {
      state.occupationList = action.payload.occupation_list
      return state
    },
    getWorkCategoryListSuccess: (state, action: PayloadAction<GetWorkCategoryListResponse>) => {
      state.occupationList = action.payload.occupation_list
      state.work_style_list = action.payload.work_style_list
      return state
    },
    patchWorkCategoriesSuccess: (state, action: PayloadAction<PatchWorkCategoriesRequest>) => {
      state.occupation_id = action.payload.occupation_id
      state.work_style_ids = action.payload.work_style_ids
      state.base_work_time = action.payload.base_work_time
      state.base_active_time = action.payload.base_active_time
      state.base_inactive_time = action.payload.base_inactive_time
    },
  },
})

export const {
  requestStart,
  getProfileSuccess,
  patchProfileSuccess,
  getOccupationListSuccess,
  requestFailure,
  requestFinish,
  getWorkCategoryListSuccess,
  patchWorkCategoriesSuccess,
} = profileSlice.actions

export const profileReducer = profileSlice.reducer

export const fetchProfile = (): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  const profile = getProfile()
  const work_category_list = getWorkCategoryList()
  Promise.all([profile, work_category_list])
    .then(([profileResult, workCategoryListResult]) => {
      dispatch(getProfileSuccess(profileResult))
      dispatch(getWorkCategoryListSuccess(workCategoryListResult))
      dispatch(requestFinish())
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateProfile = (
  params: EditProfileRequest,
  callback: () => void,
  errorCallback?: (error: ErrorCallback) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchProfile(params)
    .then(() => {
      dispatch(patchProfileSuccess(params))
      dispatch(requestFinish())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      if (errorCallback) errorCallback(error)
    })
}

export const updateOccupation = (
  params: {occupation_id: number},
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchOccupation(params)
    .then(() => {
      dispatch(requestFinish())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const updateWorkCategories = (
  params: PatchWorkCategoriesRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchWorkCategories(params)
    .then(() => {
      dispatch(patchWorkCategoriesSuccess(params))
      dispatch(requestFinish())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const updateProfileImage = (
  params: {image: string},
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchProfileImage(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const delProfileImage = (
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  deleteProfileImage()
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(setErrors(error))
      errorCallback()
    })
}
